import { useEffect, useState } from 'react';

import { getCancelVouchersByFileStatus } from '../apis';
import type { CancelVouchersByCSVStatus } from '../types/CancelVouchers';

type ProgressDetails = {
  percentage: number;
  value: number;
  max: number;
};

type UseCancelFileStatusResult = {
  statusData: CancelVouchersByCSVStatus | null;
  csv: string | null;
  progress: ProgressDetails;
};

export function useCancelFileStatus(
  massUpdateId: string | undefined,
  pollIntervalMs = 5000,
): UseCancelFileStatusResult {
  const [statusData, setStatusData] = useState<CancelVouchersByCSVStatus | null>(null);
  const [csv, setCsv] = useState<string | null>(null);
  const [progress, setProgress] = useState<ProgressDetails>({ percentage: 0, value: 0, max: 1 });

  useEffect(() => {
    if (!massUpdateId) {
      setStatusData(null);
      setCsv(null);
      return;
    }

    let cancelled = false;
    let retryHandle: ReturnType<typeof setTimeout> | undefined;

    async function fetchStatus() {
      if (cancelled) return;

      try {
        const response = await getCancelVouchersByFileStatus(massUpdateId as string);
        if (cancelled) return;

        if ('csv' in response) {
          const csvValue =
            typeof response.csv === 'string' ? response.csv : String(response.csv ?? '');
          setCsv(csvValue);
          setStatusData(null);
          setProgress({ percentage: 100, value: 1, max: 1 });
          return;
        }

        setStatusData(response);
        setCsv(null);
        setProgress(calculateProgress(response));

        const statusValue = (response.status || '').toString().toLowerCase();
        if (statusValue === 'pending' || statusValue === 'progress') {
          scheduleRetry();
        }
      } catch {
        if (cancelled) return;
        scheduleRetry();
        setProgress({ percentage: 0, value: 0, max: 1 });
      }
    }

    function scheduleRetry() {
      if (cancelled) return;
      retryHandle = setTimeout(fetchStatus, pollIntervalMs);
    }

    fetchStatus();

    return () => {
      cancelled = true;
      if (retryHandle) clearTimeout(retryHandle);
    };
  }, [massUpdateId, pollIntervalMs]);

  return { statusData, csv, progress };

  function calculateProgress(status: CancelVouchersByCSVStatus): ProgressDetails {
    const { size, remainingSize } = status;
    if (typeof size === 'number' && typeof remainingSize === 'number') {
      const processed = Math.max(size - remainingSize, 0);
      const max = size <= 0 ? 1 : size;
      const value = size <= 0 ? 1 : Math.min(processed, size);
      const percentage = size <= 0 ? 100 : Math.round(Math.min(100, (processed / size) * 100));
      return { percentage, value, max };
    }

    return { percentage: 0, value: 0, max: 1 };
  }
}
